// El despachador: qué panel abre cada herramienta, y el modal de límite que comparten —
// ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel.
//
// Se monta adentro de la columna del lienzo: las hojas y los sheets se portalean solos
// al body, y las dos capas de edición (Recortar, Marca de agua) se apoyan en el
// `relative` de la columna. `key` por celda+herramienta: cambiar de foto con un panel
// abierto es un panel nuevo, no el mismo con otra imagen.

import { useState } from 'react';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import type { GeneracionDelegada } from '../../../pages/v2/components/generacionDelegada';
import { MarcaDeAguaSobreElLienzo, RecorteSobreElLienzo } from './EdicionSobreElLienzo';
import type { ImagenNueva, PedidoDeHerramienta, PropsDeHerramienta } from './herramienta';
import { PanelAnimar } from './PanelAnimar';
import { PanelCarrusel } from './PanelCarrusel';
import { PanelCorregirVoz } from './PanelCorregirVoz';
import { PanelEditarConEstudio } from './PanelEditarConEstudio';
import { PanelDescripcion } from './PanelDescripcion';
import { PanelExpansion } from './PanelExpansion';
import { PanelMejorar } from './PanelMejorar';
import { PanelPasarA1080p } from './PanelPasarA1080p';
import type { TipoDeLimite } from './saldo';

// 'editar' es la barra de Editar del visor de escritorio (ADR-2026-10-07-el-atajo-editar-
// abre-el-visor): entra a `AccionVisor` para el atajo, pero el lienzo nunca la despacha
// (no está en el menú de la celda), así que no tiene panel.
const PANELES: Record<
  Exclude<PedidoDeHerramienta['herramienta'], 'recortar' | 'marca-de-agua' | 'editar'>,
  React.FC<PropsDeHerramienta>
> = {
  carrusel: PanelCarrusel,
  animar: PanelAnimar,
  mejorar: PanelMejorar,
  expansion: PanelExpansion,
  descripcion: PanelDescripcion,
  'corregir-voz': PanelCorregirVoz,
  estudio: PanelEditarConEstudio,
  'pasar-a-1080p': PanelPasarA1080p,
};

export const PanelDeHerramienta: React.FC<{
  pedido: PedidoDeHerramienta | null;
  onCerrar: () => void;
  onGeneracion: (g: GeneracionDelegada) => void;
  onImagenNueva: (img: ImagenNueva) => void;
  onError: (mensaje: string) => void;
}> = ({ pedido, onCerrar, onGeneracion, onImagenNueva, onError }) => {
  const [limite, setLimite] = useState<TipoDeLimite | null>(null);
  let panel: React.ReactNode = null;
  if (pedido) {
    const key = `${pedido.celda.imageId}:${pedido.herramienta}`;
    if (pedido.herramienta === 'recortar') {
      panel = <RecorteSobreElLienzo key={key} celda={pedido.celda} onCerrar={onCerrar} />;
    } else if (pedido.herramienta === 'marca-de-agua') {
      panel = <MarcaDeAguaSobreElLienzo key={key} celda={pedido.celda} onCerrar={onCerrar} />;
    } else if (pedido.herramienta === 'editar') {
      // Sin panel: es la barra del visor (ver `PANELES`). El lienzo no la pide nunca.
      panel = null;
    } else {
      const Panel = PANELES[pedido.herramienta];
      panel = (
        <Panel
          key={key}
          celda={pedido.celda}
          ratio={pedido.ratio}
          onCerrar={onCerrar}
          onGeneracion={onGeneracion}
          onImagenNueva={onImagenNueva}
          onLimite={setLimite}
          onError={onError}
        />
      );
    }
  }
  return (
    <>
      {panel}
      <SubscriptionLimitModal
        isOpen={limite !== null}
        onClose={() => setLimite(null)}
        type={limite ?? 'plan-limit-reached'}
      />
    </>
  );
};
